// Internationalization setup for the client-facing site.
// - react-i18next with browser-language detection + localStorage persistence
// - Locales: es (default), en, fr, pt, it
// - PERF: only the default locale (es) is bundled in the main entry chunk.
//   The other four locales are code-split and fetched on demand via
//   `setLanguage()` / `ensureLang()`, which the root route awaits inside
//   `beforeLoad` (server AND client) so translations are always present
//   before the first render — no raw-key flash, no hydration mismatch.
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import { es } from "./locales/es";

export const SUPPORTED_LANGS = ["es", "en", "fr", "it", "de", "pt"] as const;
export type AppLang = (typeof SUPPORTED_LANGS)[number];

/** Lazy loaders — each becomes its own chunk. */
const LOCALE_LOADERS: Record<Exclude<AppLang, "es">, () => Promise<{ default?: unknown } & Record<string, unknown>>> = {
  en: () => import("./locales/en"),
  fr: () => import("./locales/fr"),
  pt: () => import("./locales/pt"),
  it: () => import("./locales/it"),
  de: () => import("./locales/de"),
};

const inflight = new Map<string, Promise<void>>();

/**
 * Make sure the resource bundle for `lang` is loaded into i18next.
 * Safe to call repeatedly; resolves immediately when already present.
 */
export async function ensureLang(input?: string | null): Promise<AppLang> {
  const lang = normalizeLang(input);
  if (lang === "es") return lang;
  if (i18n.hasResourceBundle(lang, "translation")) return lang;
  const existing = inflight.get(lang);
  if (existing) {
    await existing;
    return lang;
  }
  const p = (async () => {
    try {
      const mod = await LOCALE_LOADERS[lang as Exclude<AppLang, "es">]();
      const bundle = (mod as Record<string, unknown>)[lang] ?? mod.default;
      if (bundle) i18n.addResourceBundle(lang, "translation", bundle, true, true);
    } catch {
      /* non-fatal — i18next falls back to es */
    } finally {
      inflight.delete(lang);
    }
  })();
  inflight.set(lang, p);
  await p;
  return lang;
}

/** Load (if needed) and activate a language. */
export async function setLanguage(input?: string | null): Promise<AppLang> {
  const lang = await ensureLang(input);
  if (i18n.language !== lang) {
    try {
      await i18n.changeLanguage(lang);
    } catch {
      /* non-fatal */
    }
  }
  return lang;
}

let initialized = false;

export function initI18n() {
  if (initialized) return i18n;
  initialized = true;

  const isBrowser = typeof window !== "undefined";

  // On the server we init synchronously with the default language so SSR
  // renders real translated strings — never raw keys ("heroHome.heroEyebrow")
  // that would then flash and be replaced after hydration. The browser
  // detector only runs on the client.
  const chain = isBrowser
    ? i18n.use(LanguageDetector).use(initReactI18next)
    : i18n.use(initReactI18next);

  // `init` is synchronous because only the inline default bundle is provided.
  void chain.init({
    resources: {
      es: { translation: es },
    },
    fallbackLng: "es",
    lng: isBrowser ? undefined : "es",
    supportedLngs: [...SUPPORTED_LANGS],
    load: "languageOnly",
    partialBundledLanguages: true,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    initAsync: false,
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      lookupLocalStorage: "mm.lang",
      caches: [],
    },
  });

  // Keep <html lang> in sync so SEO + the Bokun widget pick up the language.
  if (isBrowser) {
    i18n.on("languageChanged", (lng) => {
      document.documentElement.lang = lng.slice(0, 2);
    });
    // If the detector picked a non-default language, fetch its bundle right
    // away (root `beforeLoad` also awaits this, so render never races it).
    const detected = normalizeLang(i18n.language);
    if (detected !== "es") void ensureLang(detected);
  }

  return i18n;
}

// Eagerly initialize on module load so both the SSR render and the very
// first client render have translations available — prevents the raw-key
// flash on hydration.
initI18n();


export function normalizeLang(input?: string | null): AppLang {
  const code = (input ?? "").slice(0, 2).toLowerCase();
  return (SUPPORTED_LANGS as readonly string[]).includes(code) ? (code as AppLang) : "es";
}

export default i18n;

/** User choice: persist independently of translation loading. */
export async function chooseLanguage(input: AppLang) {
  try { localStorage.setItem("mm.lang", input); } catch { /* unavailable */ }
  try { document.cookie = `mm.lang=${input}; path=/; max-age=31536000; SameSite=Lax`; } catch { /* unavailable */ }
  await setLanguage(input);
  window.dispatchEvent(new Event("mm:preferences-changed"));
}
